import { CommonModule } from '@angular/common';
import { Component, forwardRef, inject, input, InputSignal, OnInit, Provider } from '@angular/core';
import { NG_VALUE_ACCESSOR, ReactiveFormsModule } from '@angular/forms';
import { NgbTooltipModule } from '@ng-bootstrap/ng-bootstrap';
import { UntilDestroy } from '@ngneat/until-destroy';
import { AbilityService } from 'ability';
import { I18NextPipe } from 'angular-i18next';
import {
  AlertComponent,
  CardComponent,
  CardGroupComponent,
  FormGroupComponent,
  MbsSize,
  ModalService,
  TextEllipsisComponent
} from 'mbs-ui-kit';
import { StorageTypeIconPipe } from 'mbs-ui-shared@utils/pipes/storage-type-icon.pipe';
import { RetentionPolicyStepValue } from 'wizards/models/retention-policy-models';
import { RemoteManagementWizardsService } from 'wizards/services/remote-management-wizards.service';
import { WizardStepsService } from 'wizards/services/wizard-steps.service';
import { StepBase } from 'wizards/steps/StepBase.class';
import { RetentionPolicyControlPanelComponent } from './control-panel/retention-policy-control-panel.component';
import { RetentionPolicyStepLogic } from './retention-policy-step.logic';
import { RetentionPolicyStorageInfoComponent } from './storage-info/retention-policy-storage-info.component';

const NBFRetentionPolicyStepValueAccessor: Provider = {
  provide: NG_VALUE_ACCESSOR,
  // eslint-disable-next-line @typescript-eslint/no-use-before-define
  useExisting: forwardRef(() => NBFRetentionPolicyStepComponent),
  multi: true
};

@UntilDestroy()
@Component({
  selector: 'mbs-nbf-retention-policy-step',
  templateUrl: './retention-policy-step.component.html',
  providers: [NBFRetentionPolicyStepValueAccessor],
  imports: [
    AlertComponent,
    CardComponent,
    CommonModule,
    FormGroupComponent,
    I18NextPipe,
    NgbTooltipModule,
    ReactiveFormsModule,
    TextEllipsisComponent,
    StorageTypeIconPipe,
    RetentionPolicyControlPanelComponent,
    RetentionPolicyStorageInfoComponent,
    CardGroupComponent
  ]
})
export class NBFRetentionPolicyStepComponent extends StepBase<RetentionPolicyStepValue> implements OnInit {
  protected readonly MbsSize = MbsSize;

  ability = inject(AbilityService);
  mainService: RemoteManagementWizardsService;
  private modalService = inject(ModalService);
  private i18nPipe = inject(I18NextPipe);
  private stepService = inject(WizardStepsService);

  public isHybrid: InputSignal<boolean> = input<boolean>(false);

  public retentionPolicyStepLogic: RetentionPolicyStepLogic;
  public elementSelectors = {
    id: {
      notScheduleAlert: 'NBF-Retention-not-full-alert',
      simpleInfo: 'NBF-Retention-simple-info',
      simpleBlock: 'NBF-Retention-simple-block',
      simpleBlockCount: 'NBF-Retention-simple-block-count',
      simpleBlockPeriod: 'NBF-Retention-simple-block-period',
      simpleBlockSwitcher: 'NBF-Retention-simple-block-switcher',
      simpleBlockMinPeriodAlert: 'NBF-Retention-simple-block-min-period-alert',
      simpleBlockRecommendTurnOn: 'NBF-Retention-simple-block-recommend-turn-on',
      advancedInfo: 'NBF-Retention-advanced-info',
      advancedInfoIncrementalSchedule: 'NBF-Retention-advanced-info-incremental-schedule',
      advancedInfoNoIncrementalSchedule: 'NBF-Retention-advanced-info-no-incremental-schedule',
      advancedBlock: 'NBF-Retention-advanced-block',
      advancedBlockMinPeriodAlert: 'NBF-Retention-advanced-block-min-period-alert',
      needRetentionForGFSAlert: 'NBF-Retention-need-retention-for-GFS-alert',
      switcherGFS: 'NBF-Retention-switcher-GFS',
      buttonGFS: 'NBF-Retention-button-GFS',
      storageBlock: 'NBF-Retention-storage-block',
      storageHasObjectLock: 'NBF-Retention-storage-object-lock',
      alertExpandButton: 'NBF-Retention-alert-expand-button'
    }
  };

  set retentionValue(value: RetentionPolicyStepValue) {
    this.value = value;
  }
  get retentionValue(): RetentionPolicyStepValue {
    return this.value;
  }

  constructor() {
    const mainService = inject(RemoteManagementWizardsService);

    super();
    this.mainService = mainService;
    this.retentionPolicyStepLogic = new RetentionPolicyStepLogic(this.stepService, this.modalService, this.i18nPipe, this);

    this.retentionPolicyStepLogic.initIsHybrid(this.isHybrid);
  }

  ngOnInit(): void {
    this.initForm();
    this.initEvents();
  }

  initForm(): void {
    this.stepForm = this.retentionPolicyStepLogic.getForm();

    this.initFormEvents();
  }

  private initEvents(): void {
    this.retentionPolicyStepLogic.initEvents();
  }

  protected initFormEvents(): void {
    super.initFormEvents();
    this.retentionPolicyStepLogic.initFormEvents();
  }

  onStepFormChange(value: RetentionPolicyStepValue): void {
    this.value = this.retentionPolicyStepLogic.getUpdatedValue(value);
  }

  updateForm(value: RetentionPolicyStepValue): void {
    this.retentionPolicyStepLogic.updateForm(value);

    this.isValid.emit(this.value.valid);
  }

  retentionUseDefaultSettingsChangeHandler(): void {
    this.retentionPolicyStepLogic.retentionUseDefaultSettingsChangeHandler();
  }

  retentionChangeHandler(): void {
    this.retentionPolicyStepLogic.retentionChangeHandler();
  }

  GFSEnabledChangeHandler(): void {
    this.retentionPolicyStepLogic.GFSEnabledChangeHandler();
  }

  clickDetailsGFS(needReset?: boolean): void {
    this.retentionPolicyStepLogic.clickDetailsGFS(needReset);
  }

  deleteVersionsOlderThanHybridChangeHandler(state: boolean): void {
    this.retentionPolicyStepLogic.deleteVersionsOlderThanHybridChangeHandler(state);
  }
}
